export type ProductReleaseImportance = "major" | "minor" | "patch";

export interface ProductReleaseHighlight {
  description: string;
  title: string;
}

export interface ProductRelease {
  highlights: ProductReleaseHighlight[];
  id: string;
  importance: ProductReleaseImportance;
  releasedAt: string;
  summary: string;
  title: string;
  version: string;
}

export const PRODUCT_RELEASES: readonly ProductRelease[] = [
  {
    highlights: [
      {
        title: "在网盘中直接浏览原图",
        description:
          "网盘新增小、中、大图标模式，保留详细列表和常用文件操作；图片按需加载原图，无需等待缩略图生成，并记住选择的显示方式。",
      },
      {
        title: "批量上传支持文件并发",
        description:
          "Ark 客户端可同时上传多个文件并统一限制分片并发，减少重复目录查询；遇到服务端限流时自动等待重试，并保留上传断点。",
      },
      {
        title: "恢复已经合并的上传文件",
        description:
          "上传中断后，Drive 可识别对象存储中已完成合并的文件并补交原会话，避免续传因分片会话已结束而失败，无需重新传输完整文件。",
      },
    ],
    id: "ark-2.3-drive-originals-resumable-uploads",
    importance: "minor",
    releasedAt: "2026-11-01",
    summary:
      "网盘支持多档图标直接浏览原图，Ark 批量上传可并行传输，并完善上传中断后的文件提交恢复。",
    title: "Ark 2.3：原图浏览与可靠批量上传",
    version: "2.3.0",
  },
  {
    highlights: [
      {
        title: "Skill 组与工作区上下文",
        description:
          "可将固定版本的 Skill 组合为组，并通过工作区 Profile 预览和应用依赖；本地与云端 Agent 可读取任务固定的资源和显式选择的上下文快照。",
      },
      {
        title: "统一界面与键盘操作",
        description:
          "统一常用控件、表格、导航和反馈行为，完善列表、目录树、命令菜单的键盘操作，并提供更清晰的视觉材质层级。",
      },
      {
        title: "Windows 网盘自动恢复挂载",
        description:
          "挂载成功后自动设置登录恢复，临时挂载可选择跳过；修复大视频预览阻塞和容量误报，并在意外掉线后恢复挂载。Docker 部署会同步构建和发布六平台客户端，安装器可直接获取最新版本。",
      },
    ],
    id: "ark-2.2-skill-context-drive-autostart",
    importance: "minor",
    releasedAt: "2026-10-30",
    summary:
      "新增 Skill 组与工作区上下文管理，统一界面与键盘操作，并让 Windows 网盘在登录后自动恢复挂载。",
    title: "Ark 2.2：Skill 上下文与本地网盘恢复",
    version: "2.2.0",
  },
  {
    highlights: [
      {
        title: "按请求选择 AI 响应方式",
        description:
          "AI 与 Agent 接口支持按请求选择流式或同步响应，接入方可根据交互场景选择逐段呈现或等待完整结果。",
      },
      {
        title: "拖拽整理文档层级",
        description:
          "拥有权限的文档可直接在目录树中拖拽调整层级，并阻止将文档移入自身或后代目录，减少整理文档时的操作步骤。",
      },
      {
        title: "修复 Windows 网盘读写与同步",
        description:
          "修复 Windows 挂载盘符无法写入、重命名请求被拒绝和文件下载失败的问题；在线挂载会检查元数据同步是否可用，避免误显示为空目录。",
      },
    ],
    id: "ark-2.1-document-tree-drive-fixes",
    importance: "minor",
    releasedAt: "2026-10-29",
    summary:
      "新增按请求选择 AI 响应方式和拖拽整理文档层级，并修复 Windows 本地网盘的读写、重命名与下载同步。",
    title: "Ark 2.1：文档整理与网盘同步改进",
    version: "2.1.0",
  },
  {
    highlights: [
      {
        title: "Agent 回复实时呈现并支持恢复",
        description:
          "AI 对话在 Agent 生成内容时逐段展示回复，不再等待整段输出完成；流式片段会随任务进度持久保存，页面刷新或连接恢复后可继续呈现已经生成的内容。",
      },
      {
        title: "统一交互界面全面升级",
        description:
          "基础交互控件采用统一的视觉与行为规范，下拉选择和提示信息可根据视口空间自动调整位置，减少在滚动容器或页面边缘被裁切的问题，并完善键盘操作与窄屏体验。",
      },
    ],
    id: "ark-2.0-realtime-ai-interface",
    importance: "major",
    releasedAt: "2026-10-29",
    summary:
      "Ark 正式进入 2.0：AI 回复现在可实时呈现并在连接恢复后继续显示，统一升级后的交互界面也为桌面、窄屏和键盘操作提供更一致的体验。",
    title: "Ark 2.0：实时 AI 与全新交互体验",
    version: "2.0.0",
  },
  {
    highlights: [
      {
        title: "统一客户端管理本机能力",
        description:
          "Ark 统一客户端覆盖 macOS、Linux 与 Windows 的主流架构，并兼容既有命令入口；用户可按设备管理本机 Skill、工作区和执行结果，Codex 与 TraeCode 共享一致的接入流程。",
      },
      {
        title: "Skill 支持完整目录创作与发布",
        description:
          "Skill 创作从单文件扩展为完整目录，可导入、冻结和恢复版本，并将私有创作与公共市场发布拆分为独立流程；市场页面同步改善工具发现、团队启用和安装状态展示。",
      },
      {
        title: "AI 创作建议可持续执行",
        description:
          "Skill 的逐文件建议和快速分析由后台任务持久执行，关闭页面后仍可继续；重复请求会复用既有结果，并支持查询状态、取消和失败诊断。",
      },
      {
        title: "业务页面更轻量一致",
        description:
          "核心子页面完成原生控件迁移，统一输入焦点、长文本裁剪、深色模式与移动端表现，同时减少首屏资源请求并保持微前端按需加载。",
      },
    ],
    id: "2026-10-unified-client-skill-authoring",
    importance: "minor",
    releasedAt: "2026-10-29",
    summary:
      "统一本机客户端与 Skill 管理体验，补齐完整目录创作、市场发布和可恢复的 AI 创作流程，并改善核心页面的一致性与加载效率。",
    title: "统一客户端与 Skill 创作发布",
    version: "2026.10.3",
  },
  {
    highlights: [
      {
        title: "工作流按需生成媒体并恢复图片交付",
        description:
          "工作流可选择是否生成图片、视频或语音；图片生成支持下载模型返回的图片地址，下载或写入网盘失败后可继续保存已有结果，并对图片请求排队和限流重试。",
      },
      {
        title: "网盘实时同步可断线恢复",
        description:
          "网盘元数据变更支持实时推送和从进度继续同步，配合有界日志保留与快照保护，降低断线后重新同步的成本。",
      },
      {
        title: "相册检索更灵活",
        description:
          "相册支持结合图片标签与描述进行检索，便于从已有图片和图集中找到目标内容。",
      },
    ],
    id: "2026-10-workflow-media-drive-sync",
    importance: "minor",
    releasedAt: "2026-10-24",
    summary:
      "改善工作流媒体生成与图片交付，新增可恢复的网盘实时同步，并优化相册检索体验。",
    title: "工作流媒体交付与网盘实时同步",
    version: "2026.10.2",
  },
  {
    highlights: [
      {
        description:
          "ark-bin 可将个人或团队网盘挂载到 Linux 目录，并提供依赖 WinFsp 的 Windows 盘符适配；每个挂载使用至少 4 GiB 本地缓存和写回日志，网络中断或进程重启后可继续恢复未完成的同步。",
        title: "网盘可挂载到本地文件系统",
      },
      {
        description:
          "新增独立相册应用，可按需启用网盘图片来源并显式同步；支持搜索、评分、收藏、图片标签，以及带封面和独立标签的图集管理，私有图片继续通过短期授权安全预览。",
        title: "相册与图集集中整理",
      },
      {
        description:
          "手机端重新组织 AI 会话、运行设置、通知和账户入口，复杂工作区可通过抽屉和单屏切换完成常用操作；即时通讯和 Shell 导航也同步适配窄屏交互。",
        title: "移动端工作区更易操作",
      },
    ],
    id: "2026-10-local-drive-gallery-mobile",
    importance: "minor",
    releasedAt: "2026-10-22",
    summary:
      "这一版本让个人与团队网盘进入本地文件系统，并新增可整理网盘图片的相册与图集，同时改善 AI、即时通讯和账户功能在手机端的使用体验。",
    title: "本地网盘、相册与移动工作区",
    version: "2026.10.1",
  },
  {
    highlights: [
      {
        description:
          "AI 工作台新增可视化工作流画布：主 Agent 规划任务、并行 Worker 执行、需求与质量双重审计、修订与汇总交付；个人模板可保存、更新和删除，长输出会自动收口，并兼容 DeepSeek 与 MiniMax 的思考模式差异。",
        title: "多 Agent 工作流编排",
      },
      {
        description:
          "Workflow 执行支持主动停止，历史记录可前后翻页；运行详情默认以文档排版呈现最终交付、节点过程与审计结果，并可切换画布或原始 JSON 排查问题。",
        title: "工作流执行细节可追溯",
      },
      {
        description:
          "Workflow 从对话页抽离为独立工作台；完成后的最终交付、执行过程与 Run ID 可一键写入在线文档，并在 Docs 中继续编辑和分享。",
        title: "工作流交付可沉淀为文档",
      },
      {
        description:
          "Workflow 在服务重启或执行节点接管后会从已保存进度继续，失败或超时后也可手动续跑；长时模型调用不再因单次请求达到 10 分钟而提前结束。",
        title: "工作流中断后可靠续跑",
      },
      {
        description:
          "Workflow 可为整次运行设置共享的联网搜索调用预算，并实时展示已用、总额与剩余次数；深度调研模板可直接检索外部事实。",
        title: "工作流联网检索可控可见",
      },
      {
        description:
          "Agent 生成的图片、视频与语音会归档到用户网盘，并在对话中以受权限保护的附件卡片和预览打开，不再依赖临时供应商链接。",
        title: "生成媒体可安全留存",
      },
      {
        description:
          "AI 与即时通讯的聊天框支持点击选择或直接粘贴图片和媒体，并按光标位置把图片与文字组合成同一条有序消息；即时通讯还可仅为当前用户隐藏会话，新消息到达时自动恢复显示。",
        title: "聊天支持图文混排与会话隐藏",
      },
      {
        description:
          "定时任务升级为首页可发现的独立控制台，支持创建、筛选、查看执行记录、取消，以及对失败或超时任务人工续跑；普通对话与后台调度不再混用同一入口。",
        title: "独立定时任务工作区",
      },
      {
        description:
          "AI 设置可分别为文本、图片、视频与语音选择默认模型，并展示每个模型背后的 Provider 与实际模型。",
        title: "多模态模型默认设置",
      },
      {
        description:
          "ArkOps 的 AI 模型供给改为 Provider-first：使用服务端保存的供应商地址和加密 Key 查询实时模型目录，勾选后创建逻辑路由，同时保留 JSON 批量导入。",
        title: "供应商模型批量接入",
      },
      {
        description:
          "托管 Agent、Skill Function 与本机 Codex 共享受权限约束的图片、视频、语音合成、语音转写和文档 OCR 工具。",
        title: "Agent 多模态工具贯通",
      },
      {
        description:
          "高风险工具确认可选择仅本次、当前 Session，或在 2 小时至 1 天内免重复确认；每次调用仍重新检查实时权限，会话授权在退出登录后失效。",
        title: "工具授权更灵活",
      },
      {
        description:
          "公共 Skill 发布新增来源摘要、扫描结论与平台可信签名校验，提交审核和最终发布都会重新验证，防止未受信构建产物流入市场。",
        title: "Skill 供应链发布门禁",
      },
      {
        description:
          "Skill 市场支持带确认地卸载和后续重新安装；卸载会立即移出 Agent 上下文并撤销运行密钥，同时保留历史运行与审计记录。",
        title: "Skill 安装生命周期完整",
      },
      {
        description:
          "Agent 任务可绑定网盘托管的工作区，在只读或可写边界内共享任务产物，无需把文件路径写进提示词。",
        title: "网盘托管的 Agent 工作区",
      },
      {
        description:
          "文本 Worker 可配置产物路径，完成后会把结果实际写入绑定的网盘工作区并保留路径与版本信息，后续节点只接收整理后的交付内容。",
        title: "工作流文本产物可落盘",
      },
      {
        description:
          "ArkOps 恢复服务指标、日志与链路追踪入口；Grafana 使用独立域名和自身账号登录，并支持从部署配置安全重置管理员密码。",
        title: "平台观测入口恢复",
      },
      {
        description:
          "ArkOps 初始化不再内置或预填管理员用户名与密码，部署与紧急恢复均要求显式提供账号凭据。",
        title: "系统控制台初始化更安全",
      },
      {
        description:
          "注册暂时只要求手机号与短信验证码；团队成员激活会核对已有账号的绑定手机号，并禁止手机号或邮箱在不同身份间迁移。",
        title: "注册与成员激活更安全",
      },
      {
        description:
          "在线文档和电子表格支持用 @ 提及团队成员，新增提及会发送站内通知，同时继续遵守文档原有访问权限。",
        title: "文档中可提及成员",
      },
      {
        description:
          "电子表格新增可直接创建的多维表格，支持字段排序、属性和颜色管理、单选或多选成员、视图级筛选，并可共享数据切换或删除表格、看板、甘特图和日历视图。",
        title: "多维表格与甘特图",
      },
      {
        description:
          "应用中心、审批、开发者中心与云函数补齐手机端布局；复杂数据在卡片、全屏详情或组件内滚动中清晰呈现，超长名称与 ID 仍可识别和复制。",
        title: "核心管理页面适配移动端",
      },
      {
        description:
          "AI 设置、任务中心与 Workflow 在手机端统一收纳入口；任务详情可完整滚动，Workflow 可在节点库、画布与属性面板之间单屏切换。",
        title: "AI 工作台适配移动端",
      },
      {
        description:
          "Workflow 可设置模型调用次数、Token 与费用预算，并在运行中查看消耗、剩余额度和恢复状态，避免长任务超出预期资源范围。",
        title: "Workflow 模型用量清晰可控",
      },
      {
        description:
          "本机 Agent 现可选择 Codex 或 TraeCode 执行任务；除任务中心外，普通对话也可直接选择本机设备和工作区，并在 AI 设置中统一查看设备能力、版本和实际执行器。工作台会明确区分本机与云端模型、工具和附件能力，本机审批始终保持单次授权边界。",
        title: "本机任务支持 TraeCode",
      },
      {
        description:
          "添加外部联系人时可使用对方完整且已验证的邮箱或手机号精确查找，同时继续避免模糊搜索暴露身份信息。",
        title: "外部联系人支持手机号查找",
      },
      {
        description:
          "历史 personal 用户进入 Drive、Docs 等核心页面时会自动获得完整的个人空间权限与应用，不再因旧数据初始化不完整而被拒绝访问。",
        title: "个人空间升级自动修复",
      },
      {
        description:
          "开发者应用可上传根目录包含 index.html 的 ZIP 或 TAR.GZ 前端制品，校验后生成不可变 Release，并在独立应用域名上手动激活、回滚或停用；失败的上传可修正后继续使用同一版本号重试。",
        title: "开发者应用可托管静态站点",
      },
      {
        description:
          "管理员可通过 Nacos 统一初始化和更新各服务的完整运行配置；可安全热更新的字段会即时生效，启动期字段变化会触发无状态实例替换，非法配置则保留最近一次有效版本。",
        title: "运行配置可即时生效",
      },
      {
        description:
          "发起会议时可开启无人参会自动结束并设置 0 至 30 分钟等待时间；会议从未有人加入或最后一人离开后持续空房会自动结束，AI Agent 不会被误算为真实参会者。",
        title: "空会议可自动结束",
      },
      {
        description:
          "会后二次摘要现在会生成包含核心摘要、关键要点、可勾选行动项和提示色块的在线文档；在 Docs 中可继续调整区块颜色与提示类型，HTML 和 Markdown 导出也会保留或安全降级这些语义。",
        title: "会议纪要升级为富结构文档",
      },
    ],
    id: "2026-09-agent-workflow-and-multimodal",
    importance: "minor",
    releasedAt: "2026-09-21",
    summary:
      "AI 工作台带来可联网检索的多 Agent 工作流画布、独立定时任务、多模态工具与模型批量接入，并完善网盘托管工作区、开发者应用静态站点、结构化会议纪要、运行配置治理、运行观测与系统控制台安全边界。",
    title: "多 Agent 工作流与多模态运行环境",
    version: "2026.09.4",
  },
  {
    highlights: [
      {
        description:
          "AI 设置支持接入个人 OpenAI-compatible 网关，密钥加密保存且不回显，个人模型可直接用于对话、Skill 与平台托管 Agent。",
        title: "使用自己的模型额度",
      },
      {
        description:
          "安装 ark-agent 后，可把长任务或定时任务交给本机 Codex，在指定的只读或可写工作区内执行。",
        title: "任务可在本机 Codex 执行",
      },
      {
        description:
          "本机任务支持设备撤销、租约隔离、远程取消、操作确认与 Codex thread 恢复，云端不会保存本机绝对路径。",
        title: "本机执行边界透明可控",
      },
      {
        description:
          "AI 设置集中管理本机 Codex 的安装、检查、在线状态、设备和工作区；一个 ark-agent 进程可显式托管多个项目目录，断线恢复也会在本机日志中清晰提示。",
        title: "本机 Codex 更易连接与诊断",
      },
    ],
    id: "2026-09-personal-ai-runtime",
    importance: "minor",
    releasedAt: "2026-09-20",
    summary:
      "用户现在既可以让平台 Agent 使用自己的模型网关，也可以把任务安全地交给本机 Codex。",
    title: "个人 AI 运行环境",
    version: "2026.09.3",
  },
  {
    highlights: [
      {
        description:
          "在线文档获得稳定的网盘资源映射、历史回填和统一深链；文档与网盘保留各自入口，并共享一致的权限与生命周期边界。",
        title: "文档资源稳定贯通",
      },
      {
        description:
          "Skill 市场支持 AI 辅助创建、租户安装与用户启用，Agent 可先发现轻量目录、再按需读取具体 Skill 正文，避免一次加载全部内容。",
        title: "Skill 创建与按需加载",
      },
      {
        description:
          "AI 工作台新增任务中心，可集中查看、创建和取消长任务、单次定时任务与固定间隔任务，并返回关联对话继续跟进。",
        title: "Agent 任务集中管理",
      },
      {
        description:
          "IM 会话、首页与导航接入服务端已读状态，多台设备实时合并已读进度；状态未知时明确显示同步中，不再误报为零未读。",
        title: "多端未读状态收敛",
      },
    ],
    id: "2026-09-resource-and-task-workflows",
    importance: "minor",
    releasedAt: "2026-09-20",
    summary:
      "这一版本贯通文档资源与 Skill 使用流程，并补齐 Agent 任务管理和 IM 多端未读体验。",
    title: "资源与任务协作增强",
    version: "2026.09.2",
  },
  {
    highlights: [
      {
        description:
          "AI 工作台使用统一输入入口，Agent 会根据请求自行决定立即完成、持续处理或在合适时间继续。",
        title: "Agent 自主推进任务",
      },
      {
        description:
          "页面关闭或服务重启后仍可恢复运行轨迹、等待确认和取消操作。",
        title: "任务状态可靠恢复",
      },
      {
        description:
          "Agent 可在需要时主动发送多条进度或结果消息，用户无需预先选择任务类型。",
        title: "主动反馈处理进展",
      },
      {
        description:
          "企业通讯录补齐跨字段找人、移动端组织树、新增成员、部门关系编辑和快捷发消息，并修复跨租户数据与激活安全边界。",
        title: "企业通讯录可用性重做",
      },
    ],
    id: "2026-09-agent-tasks",
    importance: "major",
    releasedAt: "2026-09-18",
    summary:
      "Agent 从一次性回答升级为可恢复、可自主推进和持续反馈的任务执行能力。",
    title: "Agent 持久任务",
    version: "2026.09.1",
  },
  {
    highlights: [
      {
        description:
          "首页聚合最近工作，全局搜索可直接定位应用、文档、会话、会议、AI 对话与审批。",
        title: "继续工作更直接",
      },
      {
        description:
          "应用中心新增真实安装概览、即时搜索、核心/扩展筛选和多入口状态。",
        title: "应用目录全面升级",
      },
      {
        description:
          "AI 工作台现在会保留 Agent 的环境准备、工具调用、确认和完成状态，不再只显示一行瞬时提示。",
        title: "Agent 执行过程可见",
      },
      {
        description:
          "核心工作区统一导航、表面层级、窄屏布局、深色模式与高对比度体验，文档编辑时还可完全收起侧边栏获得全宽空间。",
        title: "协作界面更一致",
      },
      {
        description:
          "应用可按租户、用户和客户端设置默认打开方式，在线文档也能从独立窗口稳定进入和返回。",
        title: "应用打开方式更灵活",
      },
      {
        description:
          "会议结束并完成转写后，以 100–500 字为建议目标生成二次摘要；内容较多时保留完整信息，并支持补充关注重点、重新生成和沉淀为可编辑纪要文档。",
        title: "会后纪要自动成文",
      },
    ],
    id: "2026-09-collaboration-workspace",
    importance: "major",
    releasedAt: "2026-09-16",
    summary:
      "这一版本把 Ark 的核心协作入口收拢为统一工作空间，并让应用发现与跨应用续办更清晰。",
    title: "统一协作工作台",
    version: "2026.09",
  },
  {
    highlights: [
      {
        description:
          "登录、注册、成员激活、公开文档、Drive 外链和会议邀请采用统一页面骨架。",
        title: "公共入口统一",
      },
      {
        description:
          "关键页面建立桌面、紧凑、移动、深色和高对比度浏览器视觉基线。",
        title: "多端体验可验证",
      },
    ],
    id: "2026-08-public-experience",
    importance: "minor",
    releasedAt: "2026-08-28",
    summary:
      "统一外部访问与认证体验，并将响应式、键盘操作和可访问性纳入持续验收。",
    title: "公共页面体验升级",
    version: "2026.08",
  },
];

export const MAJOR_RELEASE_PROMPT_SETTLED_EVENT =
  "ark:major-release-prompt-settled";
export const DESKTOP_UPDATE_PROMPT_SETTLED_EVENT =
  "ark:desktop-update-prompt-settled";

const seenWithoutStorage = new Set<string>();
const releasePromptEvents = new EventTarget();
let majorReleasePromptState: "active" | "pending" | "settled" = "pending";
let desktopUpdatePromptActive = false;

export function latestMajorRelease(
  releases: readonly ProductRelease[] = PRODUCT_RELEASES,
): ProductRelease | undefined {
  return releases.find((release) => release.importance === "major");
}

export function releaseAnchor(release: ProductRelease): string {
  return `release-${release.id}`;
}

export function changelogSeenKey(tenantId: string, userId: string): string {
  return `ark.changelog.seen.v1:${tenantId}:${userId}`;
}

export function unseenMajorRelease(
  tenantId: string,
  userId: string,
  storage: Pick<Storage, "getItem"> | undefined,
  releases: readonly ProductRelease[] = PRODUCT_RELEASES,
): ProductRelease | undefined {
  const release = latestMajorRelease(releases);
  if (!release || !tenantId || !userId) return undefined;
  const key = changelogSeenKey(tenantId, userId);
  const fallback = `${key}:${release.id}`;
  if (seenWithoutStorage.has(fallback)) return undefined;
  if (!storage) return release;
  try {
    if (storage.getItem(key) === release.id) return undefined;
  } catch {
    return release;
  }
  return release;
}

export function markLatestMajorReleaseSeen(
  tenantId: string,
  userId: string,
  storage: Pick<Storage, "setItem"> | undefined,
): void {
  const release = latestMajorRelease();
  if (!release || !tenantId || !userId) return;
  const fallback = `${changelogSeenKey(tenantId, userId)}:${release.id}`;
  seenWithoutStorage.add(fallback);
  try {
    storage?.setItem(changelogSeenKey(tenantId, userId), release.id);
  } catch {
    // The in-memory marker above keeps this mount from repeatedly prompting
    // when storage is disabled or unavailable.
  }
}

export function setMajorReleasePromptActive(active: boolean): void {
  majorReleasePromptState = active ? "active" : "settled";
  if (!active) {
    releasePromptEvents.dispatchEvent(
      new Event(MAJOR_RELEASE_PROMPT_SETTLED_EVENT),
    );
  }
}

export function setMajorReleasePromptPending(): void {
  majorReleasePromptState = "pending";
}

export async function activateMajorReleasePrompt(
  signal: AbortSignal,
): Promise<boolean> {
  while (!signal.aborted) {
    await waitForDesktopUpdatePrompt(signal);
    if (signal.aborted) return false;
    if (!desktopUpdatePromptActive) {
      setMajorReleasePromptActive(true);
      return true;
    }
  }
  return false;
}

export function waitForMajorReleasePrompt(signal: AbortSignal): Promise<void> {
  if (signal.aborted || majorReleasePromptState === "settled") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const finish = () => {
      releasePromptEvents.removeEventListener(
        MAJOR_RELEASE_PROMPT_SETTLED_EVENT,
        finishWhenSettled,
      );
      signal.removeEventListener("abort", finish);
      resolve();
    };
    const finishWhenSettled = () => {
      queueMicrotask(() => {
        if (majorReleasePromptState === "settled") finish();
      });
    };
    releasePromptEvents.addEventListener(
      MAJOR_RELEASE_PROMPT_SETTLED_EVENT,
      finishWhenSettled,
    );
    signal.addEventListener("abort", finish, { once: true });
  });
}

export function setDesktopUpdatePromptActive(active: boolean): void {
  desktopUpdatePromptActive = active;
  if (!active) {
    releasePromptEvents.dispatchEvent(
      new Event(DESKTOP_UPDATE_PROMPT_SETTLED_EVENT),
    );
  }
}

export async function activateDesktopUpdatePrompt(
  signal: AbortSignal,
): Promise<boolean> {
  while (!signal.aborted) {
    await waitForMajorReleasePrompt(signal);
    if (signal.aborted) return false;
    if (majorReleasePromptState === "settled") {
      setDesktopUpdatePromptActive(true);
      return true;
    }
  }
  return false;
}

export function waitForDesktopUpdatePrompt(signal: AbortSignal): Promise<void> {
  if (signal.aborted || !desktopUpdatePromptActive) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => {
      releasePromptEvents.removeEventListener(
        DESKTOP_UPDATE_PROMPT_SETTLED_EVENT,
        finish,
      );
      signal.removeEventListener("abort", finish);
      resolve();
    };
    releasePromptEvents.addEventListener(
      DESKTOP_UPDATE_PROMPT_SETTLED_EVENT,
      finish,
      { once: true },
    );
    signal.addEventListener("abort", finish, { once: true });
  });
}

export function resetMajorReleasePromptForTest(): void {
  majorReleasePromptState = "pending";
  desktopUpdatePromptActive = false;
  seenWithoutStorage.clear();
}
